import { readonly, ref } from 'vue'
import useAuth from '@/modules/auth/composables/use-auth'
import type { Workspace, WorkspaceType } from '@/types/workspaces'
import { getWorkspaces, postWorkspace, type PostWorkspaceBodyData, deleteWorkspace as deleteWorkspaceCall } from '@/api/dashboard/functions/workspaces'

// STATE
const workspaces = ref<Workspace[]>([])
const loaded = ref(false)

const pushWorkspace = (workspace: Workspace) => {
    if (!workspaces.value.find((w) => w.id === workspace.id)) {
        workspaces.value.push(workspace)
    }
    return workspace
}

const removeWorkspace = (workspaceId: string) => {
    const index = workspaces.value.findIndex((w) => w.id === workspaceId)
    workspaces.value.splice(index, 1)
    return index >= 0
}

// ACTIONS
const populateWorkspaces = async (workspaceType: WorkspaceType = 'sandbox') => {
    try {
        const response = await getWorkspaces({ workspaceType }).call()
        if (response?.data) {
            response.data.forEach(pushWorkspace)
        }
        return workspaces.value
    } finally {
        loaded.value = true
    }
}

const createWorkspace = async (workspace: PostWorkspaceBodyData) => {
    const response = await postWorkspace(
        { workspaceType: workspace.workspaceType.toLowerCase() as WorkspaceType },
        workspace,
    ).call()

    const { refreshTokenAction } = useAuth()
    await refreshTokenAction()

    return pushWorkspace(response!.data)
}

const deleteWorkspace = async (workspaceId: string) => {
    const workspace = workspaces.value.find((w) => w.id === workspaceId)!
    await deleteWorkspaceCall({
        workspaceId,
        workspaceType: workspace.workspaceType.toLowerCase() as WorkspaceType,
    }).call()
    return removeWorkspace(workspaceId)
}

// FACTORY
export default () => ({
    populateWorkspaces,
    createWorkspace,
    workspaces,
    deleteWorkspace,
    loaded: readonly(loaded),
})
